import React, { useState, useMemo, useRef, useEffect } from "react";
import { APP_IDS } from "../constants/testIds";
import { TOTAL_TICKETS } from "../utils/formatters";
import { STATUS } from "../constants/colors";

const FILTERS = [
  { key: "all", label: "All", testId: APP_IDS.filterAll },
  { key: "available", label: "Available", testId: APP_IDS.filterAvailable },
  { key: "reserved", label: "Reserved", testId: APP_IDS.filterReserved },
  { key: "sold", label: "Sold (pending)", testId: APP_IDS.filterSold },
  { key: "funds_received", label: "Funds Received", testId: "filter-funds-received" },
  { key: "returning", label: "Returning buyers", testId: "filter-returning" },
];

const MINE_FILTER = { key: "mine", label: "My Tickets", testId: "filter-mine" };

const LegendDot = ({ color, label, outline }) => (
  <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--ink-tertiary)" }}>
    <span
      className="inline-block w-2.5 h-2.5 rounded-sm"
      style={{ background: color, boxShadow: outline ? `inset 0 0 0 1px ${outline}` : undefined }}
      aria-hidden
    />
    {label}
  </span>
);

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
};

export const TicketGrid = ({ tickets, currentUser, historyOverview, yearStartedAt, onOpenTicket }) => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const isSeller = currentUser?.role === "seller";
  const filters = isSeller ? [...FILTERS, MINE_FILTER] : FILTERS;

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    // If the query has 2+ digits, also match on phone numbers (compare digits-only).
    const qDigits = q.replace(/\D/g, "");
    const phoneMode = qDigits.length >= 2;
    const out = [];
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const t = tickets[i];
      if (!t) continue;
      const buyer = (t.buyer || "").toLowerCase();
      const priorBuyer = (t.prior_buyer_name || "").toLowerCase();
      const phoneDigits = (t.phone || "").replace(/\D/g, "");
      if (
        buyer.includes(q) ||
        priorBuyer.includes(q) ||
        (phoneMode && phoneDigits && phoneDigits.includes(qDigits))
      ) {
        out.push({ n: i, t });
      }
    }
    return out.slice(0, 30); // cap for perf
  }, [tickets, search]);

  // Close the results dropdown when clicking outside the search wrapper.
  useEffect(() => {
    if (!showResults) return;
    const onDocClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showResults]);

  const visible = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const t = tickets[i];
      if (!t) continue;
      let show = false;
      if (filter === "all") show = true;
      else if (filter === "mine") show = t.assigned_seller_id === currentUser?.id;
      else if (filter === "returning") show = t.has_prior_buyer && t.status === "available";
      else show = t.status === filter;
      if (show) arr.push({ n: i, t });
    }
    return arr;
  }, [tickets, filter, currentUser]);

  // Per-filter counts so each pill can show "Available · 240" etc. Single pass
  // over the 400 tickets, so cheap on every render.
  const filterCounts = useMemo(() => {
    const c = { all: 0, available: 0, reserved: 0, sold: 0, funds_received: 0, returning: 0, mine: 0 };
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const t = tickets[i];
      if (!t) continue;
      c.all += 1;
      if (t.status in c) c[t.status] += 1;
      if (t.has_prior_buyer && t.status === "available") c.returning += 1;
      if (currentUser?.id && t.assigned_seller_id === currentUser.id) c.mine += 1;
    }
    return c;
  }, [tickets, currentUser]);

  const returningCount = historyOverview?.returning_buyers || 0;
  const ageDays = daysSince(yearStartedAt);
  const showFirstMonthBanner = returningCount > 0 && (ageDays == null || ageDays <= 30);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      {showFirstMonthBanner && (
        <div
          className="mb-4 px-4 py-3 rounded-md flex items-start gap-3"
          style={{
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-default)",
            color: "var(--ink-primary)",
          }}
          data-testid="returning-buyers-banner"
        >
          <span className="text-lg leading-none" style={{ color: "var(--status-returning-star)" }}>★</span>
          <div className="text-sm">
            <strong>{returningCount} returning buyer{returningCount === 1 ? "" : "s"}</strong>
            {" haven't renewed yet. They get first dibs in the opening month — call them before opening sales to new buyers."}
            <button
              onClick={() => setFilter("returning")}
              data-testid="returning-buyers-banner-cta"
              className="ml-3 underline font-medium"
              style={{ color: "var(--brand-primary)" }}
            >
              Show only returning
            </button>
          </div>
        </div>
      )}

      <div
        className="p-4 sm:p-5"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-card)",
        }}
      >
        <div className="mb-4 relative" ref={searchRef}>
          <label htmlFor="buyer-search" className="sr-only">Search buyer by name</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-tertiary)" }} aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              id="buyer-search"
              data-testid="buyer-search-input"
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowResults(true); }}
              onFocus={() => setShowResults(true)}
              placeholder="Search buyer name or phone…"
              className="w-full sm:w-96 pl-9 pr-8 py-2 text-sm focus:outline-none"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-md)",
                color: "var(--ink-primary)",
              }}
              autoComplete="off"
            />
            {search && (
              <button
                type="button"
                data-testid="buyer-search-clear"
                onClick={() => { setSearch(""); setShowResults(false); }}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                style={{ color: "var(--ink-tertiary)" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          {showResults && search.trim().length >= 2 && (
            <div
              data-testid="buyer-search-results"
              className="absolute z-30 mt-1 w-full sm:w-96 max-h-72 overflow-y-auto"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-md)",
                boxShadow: "var(--shadow-hover)",
              }}
            >
              {searchResults.length === 0 ? (
                <div className="px-3 py-3 text-sm" style={{ color: "var(--ink-tertiary)" }} data-testid="buyer-search-empty">
                  No buyers or phone numbers match &ldquo;{search.trim()}&rdquo;.
                </div>
              ) : (
                <ul role="listbox" aria-label="Buyer search results">
                  {searchResults.map(({ n, t }) => {
                    const s = STATUS[t.status] || STATUS.available;
                    const displayName = t.buyer || (t.prior_buyer_name ? `${t.prior_buyer_name} (prior year)` : "");
                    return (
                      <li key={n}>
                        <button
                          type="button"
                          data-testid={`buyer-search-result-${n}`}
                          onClick={() => onOpenTicket(n)}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left"
                          style={{ transition: "background-color var(--duration-fast) var(--ease-standard)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-subtle)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <span
                            className="inline-flex items-center justify-center rounded-md font-bold text-xs flex-shrink-0"
                            style={{
                              background: s.bg,
                              color: s.text,
                              width: 40,
                              height: 28,
                              boxShadow: `inset 0 0 0 1px ${s.outline}`,
                            }}
                          >
                            #{n}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium truncate" style={{ color: "var(--ink-primary)" }}>{displayName}</span>
                            <span className="block text-xs truncate" style={{ color: "var(--ink-tertiary)" }}>
                              {s.label}{t.phone ? ` · ${t.phone}` : ""}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {searchResults.length === 30 && (
                    <li className="px-3 py-2 text-xs" style={{ color: "var(--ink-tertiary)", borderTop: "1px solid var(--border-default)" }}>
                      Showing first 30 matches — refine your search for more.
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => {
              const active = filter === f.key;
              const count = filterCounts[f.key] ?? 0;
              return (
                <button
                  key={f.key}
                  data-testid={f.testId}
                  onClick={() => setFilter(f.key)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs sm:text-sm font-semibold focus:outline-none"
                  style={{
                    background: active ? "var(--brand-primary)" : "var(--surface-card)",
                    color: active ? "var(--ink-on-brand)" : "var(--ink-primary)",
                    border: `1px solid ${active ? "var(--brand-primary)" : "var(--border-default)"}`,
                    borderRadius: "var(--radius-full)",
                    transition: "background-color var(--duration-base) var(--ease-standard), border-color var(--duration-base) var(--ease-standard), color var(--duration-base) var(--ease-standard)",
                  }}
                >
                  <span>{f.label}</span>
                  <span
                    data-testid={`${f.testId}-count`}
                    className="text-[10px] sm:text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-full leading-none"
                    style={{
                      background: active ? "rgba(255,255,255,0.22)" : "var(--surface-subtle)",
                      color: active ? "var(--ink-on-brand)" : "var(--ink-secondary)",
                      minWidth: "1.5rem",
                      textAlign: "center",
                    }}
                    aria-label={`${count} ${f.label}`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 flex-wrap" style={{ color: "var(--ink-tertiary)" }}>
            <LegendDot color={STATUS.available.bg} outline={STATUS.available.outline} label="Available" />
            <LegendDot color={STATUS.reserved.bg} label="Reserved" />
            <LegendDot color={STATUS.sold.bg} outline={STATUS.sold.outline} label="Sold" />
            <LegendDot color={STATUS.funds_received.bg} label="Funds" />
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span style={{ color: "var(--status-returning-star)" }}>★</span>
              Returning buyer
            </span>
          </div>
        </div>

        <div className="grid gap-1 grid-cols-10 sm:grid-cols-[repeat(20,minmax(0,1fr))]">
          {visible.map(({ n, t }) => {
            const s = STATUS[t.status] || STATUS.available;
            const title = t.status === "sold" || t.status === "reserved" || t.status === "funds_received"
              ? `Ticket #${n} — ${t.buyer || t.assigned_seller_name || ""}`
              : t.has_prior_buyer
                ? `Ticket #${n} — last year: ${t.prior_buyer_name}`
                : `Ticket #${n}`;
            return (
              <button
                key={n}
                data-testid={APP_IDS.ticketCell(n)}
                title={title}
                aria-label={title}
                onClick={() => onOpenTicket(n)}
                className="relative aspect-square flex items-center justify-center font-bold hover:scale-[1.15] hover:z-10 focus:outline-none text-[13px] sm:text-[11px]"
                style={{
                  background: s.bg,
                  color: s.text,
                  lineHeight: 1,
                  boxShadow: `inset 0 0 0 1px ${s.outline}`,
                  borderRadius: "var(--radius-sm)",
                  transition: "transform var(--duration-fast) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard)",
                }}
              >
                {n}
                {t.has_prior_buyer && t.status === "available" && (
                  <span
                    data-testid={`returning-badge-${n}`}
                    className="absolute -top-0.5 -right-0.5 text-[10px] leading-none"
                    style={{ color: "var(--status-returning-star)" }}
                    aria-hidden
                  >
                    ★
                  </span>
                )}
              </button>
            );
          })}
          {visible.length === 0 && (
            <div className="col-span-full text-center text-sm py-12" style={{ color: "var(--ink-tertiary)" }}>
              No tickets match this filter.
            </div>
          )}
        </div>

        <p className="mt-4 text-xs" style={{ color: "var(--ink-tertiary)" }}>
          Click any ticket to see details, sell, or change its status.
        </p>
      </div>
    </section>
  );
};
